if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.toggleSubmitButton(this.submitButton.disabled, this.submitButtonText.textContent);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const quantityInput = this.quantityInput;
        if (quantityInput) {
          if (quantityInput.hasAttribute('max') && quantityInput.valueAsNumber > Number(quantityInput.max)) {
            quantityInput.value = quantityInput.max;
            const notice = this.closest('product-info')?.querySelector('[data-quantity-limit]');
            if (notice) notice.hidden = false;
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          if (quantityInput.max === '0') {
            this.toggleSubmitButton(false);
            return;
          }
          if (!quantityInput.checkValidity()) {
            quantityInput.reportValidity();
            return;
          }
        }

        this.handleErrorMessage();
        this.error = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.error = true;
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              const errorMessage = response.description || window.cartStrings.error;
              this.handleErrorMessage(errorMessage);

              if (Number(response.status) === 422) {
                return this.refreshCartAfterError(formData.get('id'), errorMessage);
              }

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.setAttribute('disabled', 'disabled');
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            return this.renderCart(response).catch(() => this.refreshCartAfterError(formData.get('id')));
          })
          .catch((e) => {
            this.error = true;
            this.handleErrorMessage(window.cartStrings.error);
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error && this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async renderCart(cartData, errorMessage) {
        const render = () => {
          CartPerformance.measure('add:paint-updated-sections', () => this.cart.renderContents(cartData));
          const drawerError = this.cart.querySelector('#CartDrawer-CartErrors');
          if (drawerError && errorMessage) drawerError.textContent = errorMessage;
        };
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal?.hasAttribute('open')) {
          await new Promise((resolve) => {
            document.body.addEventListener('modalClosed', resolve, { once: true });
            quickAddModal.hide(true);
          });
          // Let the modal's focus/scroll cleanup finish before opening the cart.
          await new Promise((resolve) => setTimeout(resolve));
        }
        render();
      }

      async refreshCartAfterError(variantId, errorMessage) {
        let cartData;
        try {
          const sectionIds = this.cart?.getSectionsToRender().map((section) => section.id) || [];
          const sectionsUrl = new URL(routes.cart_url, window.location.origin);
          sectionsUrl.searchParams.set('sections', sectionIds.join(','));
          const options = { cache: 'no-store', credentials: 'same-origin' };
          const cartResponse = await fetch(`${routes.cart_url}.js`, {
            ...options, headers: { Accept: 'application/json' },
          });
          if (!cartResponse.ok) throw new Error('Cart refresh failed');
          cartData = await cartResponse.json();
          let sections = {};
          if (sectionIds.length) {
            const sectionsResponse = await fetch(sectionsUrl, options).catch(() => null);
            if (sectionsResponse?.ok) sections = (await sectionsResponse.json()) || {};
          }

          const item = cartData.items.find((entry) => String(entry.variant_id) === String(variantId));
          const isDrawer = this.cart?.tagName === 'CART-DRAWER';
          if (isDrawer && !new DOMParser().parseFromString(sections?.['cart-drawer'] || '', 'text/html').querySelector('#CartDrawer')) {
            // Shopify can return null for individual sections even with HTTP 200.
            const drawerUrl = new URL(routes.cart_url, window.location.origin);
            drawerUrl.searchParams.set('section_id', 'cart-drawer');
            const drawerResponse = await fetch(drawerUrl, options);
            if (!drawerResponse.ok) throw new Error('Cart drawer refresh failed');
            sections['cart-drawer'] = await drawerResponse.text();
          }
          if (this.cart && cartData.item_count > 0 && (isDrawer || item)) {
            await this.renderCart({ ...cartData, id: variantId, key: item?.key, sections }, errorMessage);
          } else if (!this.cart && cartData.item_count > 0) {
            window.location = routes.cart_url;
          }
        } catch (error) {
          console.error(error);
          // Do not strand successfully added items behind an outdated drawer.
          if (cartData?.item_count > 0) window.location = routes.cart_url;
        } finally {
          if (cartData) {
            // Other widgets may fail or take time; they must not delay the drawer.
            Promise.resolve().then(() => publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form', productVariantId: variantId, cartData,
            })).catch((error) => console.error(error));
          }
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!disable && this.quantityInput?.max === '0') {
          disable = true;
          text = Number(this.quantityInput.dataset.cartQuantity) > 0
            ? this.closest('product-info')?.querySelector('[data-quantity-limit]')?.dataset.limitButtonText
            : window.variantStrings.soldOut;
        }
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      get quantityInput() {
        return this.form.querySelector('[name="quantity"]') || this.closest('product-info')?.querySelector('.quantity__input');
      }
    }
  );
}
